import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { BASEMAP, basemapUrlFor, prefersDark, onColorSchemeChange } from '../utils/basemap';
import { sendAuthenticatedRequest } from '../utils/api';
import ConfirmModal from './ConfirmModal';
import './ParkingHistory.css';

// The owner's own parking history: every park the server recorded, from parking_sessions.
//
// 2026-10-17: the phone has had this since 2026-09-17 and the web had nothing. It is also the one
// screen that is genuinely BETTER here — it is two halves that fight for room on a phone (a map of
// WHERE you park, a list of WHEN and for how long) and simply sit side by side on a laptop.
//
// A route rather than a panel or a modal, for three reasons: 340px cannot hold a map and the
// spatial half is the point; it is a place you browse rather than a decision, which is what a
// modal is for; and a route is linkable and the browser's Back button works. /ops is the
// precedent for a full-screen view in this app.
//
// Read-only and private: both endpoints filter by the caller's own id server-side and there is no
// route that serves one user's history to another. The only write is erasing your own.

const DEFAULT_CENTER = [40.6401, 22.9444]; // Thessaloniki — matches Map.js's own fallback

// --color-brand and --color-brand-light, as literals. See the pathOptions note below.
const DOT_SELECTED = '#512da8';
const DOT = '#673dcb';

const fmtDuration = (min) => {
  if (min == null) return '';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

const fmtDay = (d) =>
  d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });

const fmtTime = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

// Moves the map when the selection changes. A child of MapContainer because the map instance only
// exists inside it — and because `center` is applied at MOUNT only, which is how the dashboard map
// came to open over London in a previous life.
const MapFocus = ({ selected, bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (selected) {
      // Close enough to read the street, not so close that a GPS metre or two looks like a
      // different place.
      map.flyTo([parseFloat(selected.latitude), parseFloat(selected.longitude)], 17, { duration: 0.6 });
    } else if (bounds) {
      map.flyToBounds(bounds, { padding: [40, 40], duration: 0.6, maxZoom: 16 });
    }
  }, [selected, bounds, map]);
  return null;
};

// How far back the screen is looking. Preset chips rather than a slider or a from/to pair:
// a slider has no natural unit here (days? weeks?) and needs a label to say where it is, and a
// date range is two controls, a calendar and a validation rule for a question nobody asks in those
// terms. People ask "recently" or "all of it".
const RANGES = [
  { key: 7, label: '7 days' },
  { key: 30, label: '30 days' },
  { key: 90, label: '90 days' },
  { key: 0, label: 'All' },
];

// The server's own rule, mirrored exactly (see /api/user/parking-stats): a session with no end, or
// one whose end was never observed, is not a measurement and must not be averaged. Getting this
// subtly different here would mean the web and the phone quietly disagreed about the same park.
const isMeasured = (s) => !!s.ended_at && s.ended_reason !== 'superseded' && s.ended_reason !== 'orphan';

const ParkingHistory = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(null); // null while loading
  const [rangeDays, setRangeDays] = useState(30);
  const [failed, setFailed] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [confirmErase, setConfirmErase] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [isDark, setIsDark] = useState(prefersDark);
  const listRef = useRef(null);

  useEffect(() => onColorSchemeChange(setIsDark), []);

  const fetchHistory = useCallback(async () => {
    try {
      // 500 is the endpoint's own cap. /user/parking-stats is deliberately NOT fetched any more:
      // it computes over all time, and a screen filtered to 7 days cannot headline it with a number
      // that counts everything. The four figures are derived from the rows in view instead, by the
      // server's own rule — see isMeasured.
      const history = await sendAuthenticatedRequest('/user/parking-history?limit=500');
      setSessions(history || []);
      setFailed(false);
    } catch (e) {
      setSessions([]);
      setFailed(true);
    }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleErase = async () => {
    setConfirmErase(false);
    setErasing(true);
    try {
      await sendAuthenticatedRequest('/user/parking-history', 'DELETE');
      setSessions([]);
      setSelectedId(null);
    } catch (e) {
      setFailed(true);
    } finally {
      setErasing(false);
    }
  };

  // Everything below works from this: the list, the map and the four figures. One range, one set
  // of rows, so the numbers cannot contradict the list they sit above.
  const inRange = useMemo(() => {
    if (!sessions) return null;
    if (!rangeDays) return sessions;
    const cutoff = Date.now() - rangeDays * 86400000;
    return sessions.filter((s) => new Date(s.started_at).getTime() >= cutoff);
  }, [sessions, rangeDays]);

  const figures = useMemo(() => {
    const rows = inRange || [];
    const measured = rows.filter(isMeasured).map((r) => r.duration_minutes).filter((n) => n != null);
    return {
      parks: rows.length,
      longest: measured.length ? Math.max(...measured) : null,
      // How long was spent driving in this period. 2026-10-18: this was "last drive" — the newest
      // park with a drive attached — which is correct but barely moves, because the most recent
      // drive is inside every window that contains it. Checked by running the real figures across
      // all four ranges: parks and longest moved, this one read the same at 7, 30, 90 and All.
      //
      // A FLOOR, NOT A TOTAL, and the label says "driving" rather than "total" for that reason.
      // drive_minutes is attached only where the server could establish that the gap before a park
      // really was a drive (deriveTrips) — a missed park in between, or a move up the same street,
      // leaves it null. Those parks contribute nothing here rather than a guess, which is the same
      // rule the rows themselves follow.
      driving: rows.reduce((sum, r) => sum + (r.drive_minutes || 0), 0) || null,
    };
  }, [inRange]);

  const points = useMemo(
    () => (inRange || [])
      .map((s) => ({ ...s, lat: parseFloat(s.latitude), lon: parseFloat(s.longitude) }))
      .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lon)),
    [inRange]
  );

  // Framed on EVERYTHING rather than on the newest park alone — the point of the map here is the
  // shape of where you park.
  const bounds = useMemo(() => {
    if (!points.length) return null;
    const lats = points.map((p) => p.lat);
    const lons = points.map((p) => p.lon);
    return [[Math.min(...lats), Math.min(...lons)], [Math.max(...lats), Math.max(...lons)]];
  }, [points]);

  const selected = points.find((p) => p.id === selectedId) || null;

  // Tapping the same row again clears it and frames everything, so there is always a way back to
  // the overview without leaving the screen.
  const toggle = (id) => setSelectedId((prev) => (prev === id ? null : id));

  const stat = (v, suffix = '') => (v == null ? '—' : `${v}${suffix}`);

  return (
    <div className="history-screen">
      <header className="history-head">
        <button type="button" className="history-back" onClick={() => navigate('/dashboard')}>
          <i className="fas fa-arrow-left" aria-hidden="true" /> Back
        </button>
        <h1>Parking history</h1>
        {/* The range lives in the header, beside the count it governs, so the number is never read
            without the period it covers. */}
        <div className="history-range" role="group" aria-label="Period">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              className={`history-range-chip${rangeDays === r.key ? ' is-on' : ''}`}
              aria-pressed={rangeDays === r.key}
              onClick={() => { setRangeDays(r.key); setSelectedId(null); }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      <div className="history-body">
        <div className="history-map">
          {points.length > 0 ? (
            <MapContainer center={DEFAULT_CENTER} zoom={13} style={{ height: '100%', width: '100%' }}>
              {/* See utils/basemap.js. `key` forces a clean remount when the theme flips — a
                  TileLayer's url updates in place, but the other scheme's cached tiles flash
                  through first. */}
              <TileLayer
                key={isDark ? 'dark' : 'light'}
                url={basemapUrlFor(isDark)}
                attribution={BASEMAP.attribution}
                subdomains={BASEMAP.subdomains}
                maxZoom={BASEMAP.maxZoom}
              />
              <MapFocus selected={selected} bounds={bounds} />
              {points.map((p) => {
                const isSel = p.id === selectedId;
                return (
                  <CircleMarker
                    key={p.id}
                    center={[p.lat, p.lon]}
                    radius={isSel ? 11 : 6}
                    // Literal hex, not var(--color-brand): Leaflet writes these straight onto the
                    // SVG as presentation attributes, and var() is not substituted in those — the
                    // dots would have come out with no colour at all. Same two purples as the
                    // tokens, which is why they are named here rather than picked.
                    pathOptions={{
                      color: isSel ? DOT_SELECTED : DOT,
                      fillColor: isSel ? DOT_SELECTED : DOT,
                      fillOpacity: isSel ? 0.95 : 0.45,
                      weight: isSel ? 3 : 1,
                    }}
                    eventHandlers={{ click: () => toggle(p.id) }}
                  />
                );
              })}
            </MapContainer>
          ) : (
            <div className="history-map-empty">
              {sessions == null ? 'Loading your history…' : 'Nothing recorded yet.'}
            </div>
          )}
        </div>

        <aside className="history-side" ref={listRef}>
          {/* The same four-chip strip as the profile, for the same reason: four numbers read faster
              side by side than as a list of labelled rows. */}
          <div className="profile-stats history-stats">
            {/* Three, not four. "This week" was one of them, and the range selector above now
                answers that question properly — leaving it would be two controls for one idea. */}
            <div className="profile-stat">
              <span className="profile-stat-number">{stat(sessions ? figures.parks : null)}</span>
              <span className="profile-stat-label">Parks</span>
            </div>
            {/* NOT an average stay, deliberately. ParkingHistoryScreen.js rejected that figure and
                the reasoning holds here: it pools overnight at home with twenty minutes at a shop,
                so the mean describes no park this driver has ever made. The same number IS
                meaningful per PLACE, which is where placeLearning computes it. */}
            <div className="profile-stat">
              <span className="profile-stat-number">{figures.driving != null ? fmtDuration(figures.driving) : '—'}</span>
              <span className="profile-stat-label">Driving</span>
            </div>
            <div className="profile-stat">
              <span className="profile-stat-number">{figures.longest != null ? fmtDuration(figures.longest) : '—'}</span>
              <span className="profile-stat-label">Longest</span>
            </div>
          </div>

          {failed && sessions?.length === 0 && (
            <p className="panel-empty">Could not load your history. Nothing has been changed.</p>
          )}

          {!failed && sessions?.length === 0 && (
            <p className="panel-empty">
              No parks recorded yet.
              <br />
              Udal writes one here every time it works out where you parked.
            </p>
          )}

          {/* Distinct from having no history at all — "nothing in this period" is answered by
              changing the period, and saying so is better than an empty list with no explanation. */}
          {!failed && sessions?.length > 0 && inRange?.length === 0 && (
            <p className="panel-empty">
              No parks in the last {rangeDays} days.
              <br />
              You have {sessions.length} older {sessions.length === 1 ? 'one' : 'ones'} — try a wider period.
            </p>
          )}

          <div className="history-list">
            {(inRange || []).map((s) => {
              const started = new Date(s.started_at);
              const open = !s.ended_at;
              // An end nobody observed is not a measurement — see the endpoint's own note.
              const approx = s.ended_reason === 'orphan' || s.ended_reason === 'superseded';
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`history-row${s.id === selectedId ? ' is-selected' : ''}`}
                  onClick={() => toggle(s.id)}
                >
                  <i
                    className={`history-row-icon fas ${s.is_auto_detected ? 'fa-car' : 'fa-map-pin'}`}
                    aria-hidden="true"
                    title={s.is_auto_detected ? 'Detected automatically' : 'Placed by hand'}
                  />
                  <span className="history-row-text">
                    <span className="history-row-title">{fmtDay(started)}</span>
                    <span className="history-row-sub">
                      {fmtTime(started)}
                      {open ? ' · still parked' : ` · ${fmtDuration(s.duration_minutes)}`}
                      {approx ? ' (approx.)' : ''}
                    </span>
                    {/* The drive that ENDED at this park, when the server could establish one.
                        Absent on the oldest row in the window and wherever the gap did not qualify
                        as a drive. Nothing rather than a guess: a list like this is exactly where a
                        wrong number looks most authoritative. */}
                    {s.drive_minutes != null && (
                      <span className="history-row-drive">After a {fmtDuration(s.drive_minutes)} drive</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          {inRange?.length > 0 && (
            <div className="history-erase">
              <button
                type="button"
                className="btn btn--danger-quiet btn--sm btn--block"
                onClick={() => setConfirmErase(true)}
                disabled={erasing}
              >
                {erasing ? 'Erasing…' : 'Erase history'}
              </button>
            </div>
          )}
        </aside>
      </div>

      <ConfirmModal
        isOpen={confirmErase}
        title="Erase parking history?"
        message="Every past park is deleted from the server. Where your car is now is not affected. This cannot be undone."
        confirmLabel="Erase"
        tone="danger"
        onConfirm={handleErase}
        onClose={() => setConfirmErase(false)}
      />
    </div>
  );
};

export default ParkingHistory;
